import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DownloadOutlined as DownloadOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  Search as SearchIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
} from '@mui/icons-material';
import axiosInstance from '../../api/axiosConfig';
import { getApiErrorMessage } from '../../api/errorUtils';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';
import EmptyState from '../common/EmptyState';
import PageContainer from '../common/PageContainer';
import { styled } from '@mui/system';

const ListSurface = styled(AdminPanelCard)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 6,
  background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
}));

const SoftTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 6,
  border: 'none',
  backgroundColor: '#ffffff',
  overflow: 'hidden',
  '& .MuiTable-root': {
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  '& .MuiTableHead-root .MuiTableRow-root': {
    backgroundColor: '#f5f7fb',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    borderBottom: '1px solid rgba(148, 163, 184, 0.18)',
    color: '#64748b',
    fontSize: '0.78rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    backgroundColor: '#f5f7fb',
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    backgroundColor: '#ffffff',
    transition: 'background-color 0.2s ease',
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover': {
    backgroundColor: '#f8fbff',
  },
  '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
    borderBottom: 'none',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: '1px solid rgba(226, 232, 240, 0.75)',
    borderLeft: 'none',
    borderRight: 'none',
    fontSize: '0.86rem',
    color: theme.palette.text.primary,
    paddingTop: theme.spacing(0.9),
    paddingBottom: theme.spacing(0.9),
  },
}));

const Employees = () => {
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showNotification = useCallback((message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  }, []);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/employees');
      setEmployees(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setEmployees([]);
      showNotification(getApiErrorMessage(error, 'Failed to fetch employees.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  const fetchRoles = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/roles');
      const list = Array.isArray(response.data) ? response.data : [];
      setRoles(list);
    } catch (error) {
      setRoles([]);
      showNotification(getApiErrorMessage(error, 'Failed to fetch role options.'), 'warning');
    }
  }, [showNotification]);

  useEffect(() => {
    fetchEmployees();
    fetchRoles();
  }, [fetchEmployees, fetchRoles]);

  const filteredEmployees = useMemo(() => {
    const byRole = employees.filter((employee) => {
      if (!selectedRole) return true;
      const employeeRoles = String(employee?.role || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      return employeeRoles.includes(selectedRole);
    });

    if (!searchTerm.trim()) return byRole;
    const search = searchTerm.trim().toLowerCase();
    return byRole.filter((employee) => {
      const name = `${employee?.firstName || ''} ${employee?.lastName || ''}`.toLowerCase();
      return (
        name.includes(search) ||
        String(employee?.employeeCode || '').toLowerCase().includes(search) ||
        String(employee?.phone || '').toLowerCase().includes(search)
      );
    });
  }, [employees, searchTerm, selectedRole]);
  const totalEmployees = filteredEmployees.length;

  const handleExportEmployees = async () => {
    setExporting(true);
    try {
      if (!filteredEmployees.length) {
        showNotification('No employees found to export.', 'info');
        return;
      }

      const rows = [
        ['Employee Code', 'Name', 'Phone', 'Status', 'Roles'],
        ...filteredEmployees.map((employee) => [
          employee?.employeeCode || '',
          `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim(),
          employee?.phone || '',
          employee?.status || '',
          String(employee?.role || ''),
        ]),
      ];

      const csvContent = rows
        .map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `employees_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showNotification('Employee list exported successfully.', 'success');
    } catch (error) {
      showNotification(getApiErrorMessage(error, 'Failed to export employee list.'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const headerActions = (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Button
        variant="outlined"
        size="small"
        startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlinedIcon />}
        onClick={handleExportEmployees}
        disabled={loading || exporting}
      >
        Export List
      </Button>
      <Button component={Link} to="/teacher/add" variant="contained" size="small" startIcon={<AddIcon />} disabled={loading}>
        Add Employee
      </Button>
    </Stack>
  );

  return (
    <PageContainer>
      <AdminPageHero
        title="Employees"
        subtitle="Manage employee profiles, roles, and working records from one directory."
        actions={headerActions}
        metrics={[
          { label: 'Visible', value: totalEmployees },
          { label: 'Roles', value: roles.length },
        ]}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={notification.severity}
          variant="filled"
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <ListSurface
        title="Employee Directory"
        subtitle="Filter by role or search by name, code, or phone number."
      >
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select value={selectedRole} label="Role" onChange={(event) => setSelectedRole(event.target.value)}>
                <MenuItem value="">All Roles</MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {String(role).replace(/_/g, ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Name, code, or phone"
              value={searchTerm}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </Grid>
        </Grid>
      </ListSurface>

      <ListSurface
        title="Employee Records"
        subtitle="Open staff profiles, verify status, and manage role assignments."
        contentSx={{ p: 0 }}
      >
        <SoftTableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Employee Code</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : !filteredEmployees.length ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <EmptyState title="No employees found" description="Try a different role or search keyword." />
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmployees.map((employee, index) => {
                  const rolesText = String(employee?.role || '');
                  const roleTokens = rolesText
                    .split(',')
                    .map((token) => token.trim())
                    .filter(Boolean);
                  const status = String(employee?.status || '').toUpperCase();
                  const isActive = status === 'ACTIVE';
                  return (
                    <TableRow hover key={employee.employeeId}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <Avatar sx={{ width: 34, height: 34 }}>
                            {`${employee?.firstName?.[0] || ''}${employee?.lastName?.[0] || ''}`.toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {`${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {employee?.employeeCode || 'Code N/A'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{employee?.employeeCode || '-'}</TableCell>
                      <TableCell>{employee?.phone || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={isActive ? 'success' : 'default'}
                          label={status || 'N/A'}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                          {roleTokens.length ? (
                            roleTokens.map((role, roleIndex) => (
                              <Chip key={`${employee.employeeId}-${roleIndex}`} size="small" label={role.replace(/_/g, ' ')} variant="outlined" />
                            ))
                          ) : (
                            <Chip size="small" label="N/A" variant="outlined" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Employee">
                          <IconButton size="small" onClick={() => navigate(`/employee/${employee.employeeId}`)}>
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Employee">
                          <IconButton size="small" onClick={() => navigate(`/teacher/edit/${employee.employeeId}`)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </SoftTableContainer>
      </ListSurface>
    </PageContainer>
  );
};

export default Employees;
